import type { KnownNumberView, PhoneContactsStatus, PhoneSpamStatus } from "@vaulted/shared";
import { formatPhone } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, patch, post } from "../../api";
import { Alert, Badge, Button, Checkbox, Field, Loading, Panel, PanelHeader, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";
import { DeskPhones } from "./DeskPhones";

/** Numbers that aren't clients: named ones (vendors, offices) and blocked spam. */
export function PhoneTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["numbers"], queryFn: () => get<KnownNumberView[]>("/api/numbers") });
  const refresh = () => Promise.all(["numbers", "calls", "comm", "calls-ringing", "spam-rule"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
  const spam = useQuery({ queryKey: ["spam-rule"], queryFn: () => get<PhoneSpamStatus>("/api/numbers/spam-rule"), refetchInterval: 30_000 });
  const update = useMutation({ mutationFn: (v: { id: string; name: string; blocked: boolean }) => patch(`/api/numbers/${v.id}`, { name: v.name, blocked: v.blocked }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/numbers/${id}`), onSuccess: refresh });
  const [adding, setAdding] = useState<{ phone: string; name: string; blocked: boolean } | null>(null);
  const add = useMutation({
    mutationFn: () => post("/api/numbers", adding),
    onSuccess: async () => {
      setAdding(null);
      await refresh();
    },
  });
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const err = update.error ?? remove.error ?? add.error;
  return (
    <div className="flex flex-col gap-6">
      <ContactsSync />
      <DeskPhones />
      <Panel>
        <PanelHeader
          title="Numbers that aren't clients"
          description="Named numbers (a vendor, a doctor's office) show by name instead of as an unknown caller. Blocked numbers go straight to voicemail in RingCentral (nobody's phone rings), don't pop up, and their calls, voicemails, and texts are marked read as they come in (they're still kept; texts still arrive)."
        />
        {spam.data && spam.data.blocked > 0 && <SpamRuleNote s={spam.data} />}
        <div className="flex flex-col gap-3 px-5 py-5">
          {list.isLoading ? (
            <Loading />
          ) : list.data?.length ? (
            <ul className="flex flex-col divide-y divide-mist">
              {list.data.map((n) => (
                <li key={n.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <span className="font-semibold">{formatPhone(n.phone)}</span>
                  {editing?.id === n.id ? (
                    <form
                      className="flex flex-wrap items-end gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        update.mutate({ id: n.id, name: editing.name, blocked: n.blocked }, { onSuccess: () => setEditing(null) });
                      }}
                    >
                      <Field label="Name" maxLength={80} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                      <Button variant="secondary" type="submit" busy={update.isPending}>
                        Save
                      </Button>
                      <Button variant="quiet" type="button" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </form>
                  ) : (
                    <span className="text-ink-soft">{n.name || "—"}</span>
                  )}
                  {n.blocked && <Badge tone="off">Blocked</Badge>}
                  <span className="text-xs text-muted">
                    {formatDateTime(n.createdAt)}
                    {n.addedBy ? ` by ${n.addedBy}` : ""}
                  </span>
                  <span className="ml-auto flex flex-wrap gap-1">
                    <Button variant="quiet" onClick={() => setEditing({ id: n.id, name: n.name })}>
                      Rename
                    </Button>
                    {n.blocked ? (
                      <Button variant="quiet" busy={update.isPending} onClick={() => update.mutate({ id: n.id, name: n.name, blocked: false })}>
                        Unblock
                      </Button>
                    ) : (
                      <Button
                        variant="quiet"
                        busy={update.isPending}
                        onClick={() => window.confirm(`Block ${formatPhone(n.phone)} as spam? Its calls go straight to voicemail.`) && update.mutate({ id: n.id, name: n.name, blocked: true })}
                      >
                        Block
                      </Button>
                    )}
                    <Button variant="quiet" busy={remove.isPending} onClick={() => window.confirm(`Forget ${formatPhone(n.phone)}? It will show as an unknown number again.`) && remove.mutate(n.id)}>
                      Remove
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">None yet. Mark a caller "Not a client" or "Block" from the pop-up or the Phone list.</p>
          )}
          {adding ? (
            <form
              className="grid gap-3 sm:grid-cols-6"
              onSubmit={(e) => {
                e.preventDefault();
                if (adding.blocked && !window.confirm(`Block ${adding.phone} as spam?`)) return;
                add.mutate();
              }}
            >
              <Field label="Number" type="tel" required className="sm:col-span-2" value={adding.phone} onChange={(e) => setAdding({ ...adding, phone: e.target.value })} />
              <Field label="Name" className="sm:col-span-2" placeholder="McKesson" maxLength={80} value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} />
              <label className="flex items-end gap-2 pb-2 text-sm sm:col-span-2">
                <input type="checkbox" className="size-4 accent-[var(--color-brand)]" checked={adding.blocked} onChange={(e) => setAdding({ ...adding, blocked: e.target.checked })} />
                Block (spam)
              </label>
              <div className="flex gap-2 sm:col-span-6">
                <Button variant="secondary" type="submit" busy={add.isPending}>
                  Add
                </Button>
                <Button variant="quiet" type="button" onClick={() => setAdding(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <button type="button" className="self-start text-sm font-semibold text-brand hover:text-brand-deep" onClick={() => setAdding({ phone: "", name: "", blocked: false })}>
              + Add a number
            </button>
          )}
          {err && <Alert>{errorMessage(err)}</Alert>}
        </div>
      </Panel>
    </div>
  );
}

/** Keep clients (and named numbers) in the texting user's RingCentral contacts, so calls and texts show by name in the RingCentral app. */
function ContactsSync() {
  const qc = useQueryClient();
  const { can } = useSession();
  const status = useQuery({ queryKey: ["contacts-sync"], queryFn: () => get<PhoneContactsStatus>("/api/numbers/contacts-sync"), refetchInterval: 60_000 });
  const toggle = useMutation({
    mutationFn: (on: boolean) => patch("/api/settings", { "phone.contactsSync": on }),
    onSuccess: () => Promise.all(["contacts-sync", "settings"].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
  const release = useMutation({
    mutationFn: () => post<PhoneContactsStatus>("/api/numbers/contacts-sync/release", {}),
    onSuccess: () => Promise.all(["contacts-sync", "settings"].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
  const s = status.data;
  return (
    <Panel>
      <PanelHeader
        title="RingCentral contacts"
        description="Adds every client's numbers (and the named numbers below) to the texting user's RingCentral contacts, so calls and texts show who it is in the RingCentral app, even away from Vaulted. One contact per number, named for whose mobile it is, with anyone else on it in brackets. Kept up to date as clients change; turning it off removes them. Only contacts Vaulted added are ever changed."
      />
      <div className="flex flex-col gap-3 px-5 py-5 text-sm">
        {!s ? (
          <Loading />
        ) : (
          <>
            {!s.allowed && (
              <Alert tone="info">
                Only production keeps RingCentral contacts: this copy of Vaulted shares the clinic's real RingCentral account, so it makes no contact changes.
                {s.enabled && s.inPhoneSystem > 0 && " Use “Stop, keep the contacts” once production has taken them over."}
              </Alert>
            )}
            <Checkbox
              label="Keep clients in RingCentral contacts"
              checked={s.enabled}
              disabled={!can("settings.manage") || toggle.isPending || (!s.allowed && !s.enabled)}
              onChange={(on) => (on || window.confirm("Remove the contacts Vaulted added from RingCentral?")) && toggle.mutate(on)}
            />
            {s.enabled ? (
              <p className="text-ink-soft">
                {s.upToDate} of {s.numbers} numbers in RingCentral{s.upToDate < s.numbers ? ". About 8 are added each minute (RingCentral's limit), so the first time takes a while." : "."}
              </p>
            ) : (
              s.inPhoneSystem > 0 && <p className="text-ink-soft">Removing {s.inPhoneSystem} contacts from RingCentral, about 8 a minute.</p>
            )}
            {s.enabled && can("settings.manage") && (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="quiet" busy={release.isPending} onClick={() => window.confirm("Stop keeping contacts, and leave the ones in RingCentral as they are?") && release.mutate()}>
                  Stop, keep the contacts
                </Button>
                <span className="text-xs text-muted">For handing them to another copy of Vaulted: nothing is removed from RingCentral.</span>
              </div>
            )}
            {release.error && <Alert>{errorMessage(release.error)}</Alert>}
            {s.lastError && <Alert>RingCentral: {s.lastError}. If it says the permission is missing, add "Contacts" to the Vaulted app in the RingCentral developer console.</Alert>}
            {s.lastRunAt && <p className="text-xs text-muted">Last checked {formatDateTime(s.lastRunAt)}.</p>}
          </>
        )}
        {toggle.error && <Alert>{errorMessage(toggle.error)}</Alert>}
      </div>
    </Panel>
  );
}

/** Whether RingCentral is sending the blocked numbers to voicemail yet. */
function SpamRuleNote({ s }: { s: PhoneSpamStatus }) {
  const text = !s.allowed
    ? "This copy of Vaulted doesn't change RingCentral (only production does), so here blocked numbers are only quieted in Vaulted."
    : s.lastError
      ? `RingCentral isn't sending blocked numbers to voicemail yet: ${s.lastError}`
      : s.upToDate
        ? `RingCentral sends these ${s.blocked} blocked number${s.blocked > 1 ? "s" : ""} straight to voicemail (rule "Vaulted spam → voicemail"${s.syncedAt ? `, updated ${formatDateTime(s.syncedAt)}` : ""}).`
        : "Updating RingCentral (within a minute or two)…";
  return (
    <div className="px-5 pt-1">
      <Alert tone={s.lastError ? "danger" : "info"}>{text}</Alert>
    </div>
  );
}
